// src/shared/utils/imageFitPad.ts
//
// Fit-and-pad for transparent logo uploads (plan 2.7): the server's pfile
// transcode cover-crops to the slot's size, so a mark whose aspect differs
// from the slot would silently lose its sides. Delivering the EXACT canvas
// from the browser — the mark scaled to fit, the rest transparent — makes
// that crop a no-op. The geometry is pure and unit-tested; the canvas/File
// plumbing at the bottom stays thin. Each slot picks its canvas and where the
// mark sits inside it (the anchor):
//   - 'bottom-left': the series title logo (ADR-0013) — the shells draw the
//     canvas with its left edge at the content margin and its bottom on the
//     typed title's bottom, so padding goes right and up;
//   - 'left-center': a top-bar brand logo — left-aligned, centered on the
//     bar's height.

export interface FitPadCanvas {
  w: number;
  h: number;
}

export type FitPadAnchor = 'bottom-left' | 'left-center';

export interface FitPadGeometry {
  /** Scale applied to the source: up or down, so the mark fills the canvas in one direction. */
  scale: number;
  drawW: number;
  drawH: number;
  /** Draw origin inside the canvas per the anchor. */
  dx: number;
  dy: number;
}

export class ImageFitPad {
  /**
   * Fit the mark to the canvas, aspect preserved, scaling UP or DOWN so it
   * fills the canvas in one direction (the consumer renders the delivered
   * canvas at its own size or smaller — the never-enlarge rule belongs to the
   * consumer's slot, not to this step). Placement per `anchor`.
   */
  static geometry(srcW: number, srcH: number, canvas: FitPadCanvas, anchor: FitPadAnchor): FitPadGeometry {
    if (srcW <= 0 || srcH <= 0 || canvas.w <= 0 || canvas.h <= 0) {
      return { scale: 1, drawW: 0, drawH: 0, dx: 0, dy: anchor === 'bottom-left' ? canvas.h : 0 };
    }
    const scale = Math.min(canvas.w / srcW, canvas.h / srcH);
    const drawW = Math.min(canvas.w, Math.round(srcW * scale));
    const drawH = Math.min(canvas.h, Math.round(srcH * scale));
    const dy = anchor === 'bottom-left' ? canvas.h - drawH : Math.round((canvas.h - drawH) / 2);
    return { scale, drawW, drawH, dx: 0, dy };
  }

  /** True when the source already is the exact canvas — no redraw needed. */
  static isExactCanvas(srcW: number, srcH: number, canvas: FitPadCanvas): boolean {
    return srcW === canvas.w && srcH === canvas.h;
  }

  /** `.png` filename for the processed upload, keeping the customer's base name. */
  static processedFilename(original: string, fallback: string): string {
    const base = original.replace(/\.[^.]+$/, '') || fallback;
    return `${base}.png`;
  }

  static isPngFile(file: { type?: string; name: string }): boolean {
    if (file.type === 'image/png') return true;
    return /\.png$/i.test(file.name);
  }

  // ── Browser plumbing (thin; the math above is what the tests pin) ──

  /**
   * Fit-and-pad the bitmap into a transparent PNG File of the canvas size.
   * Returns the original file untouched when it already is the exact canvas
   * as a PNG.
   */
  static async toPng(
    file: File,
    bitmap: ImageBitmap,
    canvas: FitPadCanvas,
    anchor: FitPadAnchor,
    fallbackName: string,
    label: string,
  ): Promise<File> {
    if (ImageFitPad.isExactCanvas(bitmap.width, bitmap.height, canvas) && ImageFitPad.isPngFile(file)) {
      return file;
    }
    const g = ImageFitPad.geometry(bitmap.width, bitmap.height, canvas, anchor);
    const el = document.createElement('canvas');
    el.width = canvas.w;
    el.height = canvas.h;
    const ctx = el.getContext('2d');
    if (!ctx) throw new Error(`Could not prepare the ${label} (no canvas support).`);
    ctx.clearRect(0, 0, canvas.w, canvas.h);
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(bitmap, g.dx, g.dy, g.drawW, g.drawH);
    const blob = await new Promise<Blob | null>((resolve) => el.toBlob(resolve, 'image/png'));
    if (!blob) throw new Error(`Could not encode the ${label} as PNG.`);
    return new File([blob], ImageFitPad.processedFilename(file.name, fallbackName), { type: 'image/png' });
  }
}
